import React from "react";

import useEmojiGame from "./hooks/useEmojiGame";

import { usePDActiveSubscription } from '../../purpledoor/hooks/pdActiveSubscription';
import Gumball from "../gumball/Gumball";
import GumballHistoryPage from "../components/GumballHistoryPage"; 

import EmojiGameScreen from "./utils/EmojiGameScreen";
import EmojiResultScreen from "./utils/EmojiResultScreen";
import EmojiLeaderboard from "./utils/EmojiLeaderboard";
import EmojiScoreHistory from "./utils/EmojiScoreHistory";
import EmojiSettings from "./utils/EmojiSettings";
import EmojiTutorial from "./utils/EmojiTutorial";
import EmojiDailyChallenge from "./utils/EmojiDailyChallenge";
import EmojiAchievements from "./utils/EmojiAchievements";
import EmojiStreak from "./utils/EmojiStreak";

function WonderEmojiHomeScreen({ game }) {
  const stats = game.myStats || {};
  const best = stats.bestScore || {};
  const streak = game.currentStreak || 0;
  const grid = game.gridSize || 3;
  const category = game.category || "animals";
  const speed = game.speed || "medium";

  const {
      subscriptions,
      selectedSubscription,
      loadingSubs,
      product,
      isActive,
      daysLeft,
      formatRange,
      refetch
  } = usePDActiveSubscription();

  if (loadingSubs) return <p>Loading subscription...</p>;

  return (
    <div className="mt-2 min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-black text-white overflow-y-auto border-y-2 border-purple-400 rounded-2xl" tabIndex={-1}>
      <div className="sticky top-0 z-30 bg-slate-950/90 backdrop-blur border-b border-cyan-500/20">
        <div className="flex items-center justify-between px-2 py-1">
          <div>
            <div className="text-xl lg:text-2xl font-black text-cyan-300">
              😀 WonderEmojis
            </div>
            <div className="text-xs text-slate-200">Concentrate • Hunt • Smile</div>
          </div>

          <div className="text-right">
            <div className="font-black text-orange-400 mt-4 text-sm lg:text-lg">Current 🔥 {streak}</div>
          </div>
        </div>
      </div>

      <div className="px-3 pt-2">
        <div className="rounded-xl bg-gradient-to-r from-cyan-600 via-violet-600 to-pink-600 p-[1px]">
          <div className="rounded-2xl bg-slate-900 px-4 py-1">
            <div className="text-center text-xl lg:text-2xl ">🎯</div>
            <div className="mt-2 text-center text-lg lg:text-xl font-black">
              Ready?
            </div>
            <div className="mt-2 flex flex-col items-center">
              {isActive ? (
                <div className="text-center bg-green-900/30 border border-green-500/50 rounded-2xl px-2 py-1 max-w-md">
                  <div className="flex items-center justify-center gap-2 text-green-400 mb-1">
                    <span className="text-lg lg:text-xl">🎉</span>
                    <span className="font-semibold text-sm lg:text-lg">
                      Active Subscription -
                      Days Left: <span className="font-bold text-sm lg:text-lg text-white">{daysLeft}</span>
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center bg-red-900/30 border border-red-500/50 rounded-2xl px-2 py-1 max-w-md">
                  <div className="flex items-center justify-center gap-2 text-red-400 mb-1">
                    <span className="text-sm lg:text-lg">⚠️</span>
                    <span className="font-semibold text-sm lg:text-lg">No Active Subscription</span>
                  </div>
                  <p className="text-red-300 text-sm lg:text-lg mt-1">
                    Your subscription has expired or is inactive
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="px-3 mt-5">

          <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4">
            <div className="font-bold text-cyan-300 mb-4 text-center">Current Game</div>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl bg-slate-800 p-3 text-center">
                <div className="text-xs text-slate-500">Grid</div>
                <div className="font-black">
                  🟦 {grid}×{grid}
                </div>
              </div>
              <div className="rounded-xl bg-slate-800 p-3 text-center">
                <div className="text-xs text-slate-500">Category</div>
                <div className="font-black capitalize">🌈 {category}</div>
              </div>
              <div className="rounded-xl bg-slate-800 p-3 text-center">
                <div className="text-xs text-slate-500">Speed</div>
                <div className="font-black capitalize">⚡ {speed}</div>
              </div>
              </div>
            </div>
          </div>

          <div className="flex flex-row gap-4 justify-center mt-2">
              <button
                onClick={game.startGame}
                className="justify-center rounded-xl bg-cyan-500 w-fit px-2 py-1 text-sm lg:text-lg font-black text-black active:scale-95 transition"
              >
                 
                <div className="text-lg lg:text-xl">▶</div>
                <div className="text-sm lg:text-lg text-blue-800">START</div>
                <div className="text-xs lg:text-sm text-gray-50">No Stopping!! Have Fun.</div>
              </button>
              <button
                onClick={(e) => { e.currentTarget.blur();game.setScreen("settings");}}
                className="justify-center rounded-xl bg-cyan-500 w-fit px-2 py-1 text-sm lg:text-lg font-black text-black active:scale-95 transition"
              >
                <div className="text-lg lg:text-xl">⚙️</div>
                <div className="text-sm lg:text-lg text-blue-800">Settings</div>
                <div className="text-xs lg:text-sm text-gray-50">Grid, Category & Speed</div>
              </button>
          </div>
        </div>
      </div>

      <div className="px-2 mt-2 border border-gray-700">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <button
            onClick={(e) => {e.currentTarget.blur(); game.setScreen("history");}}
            className="rounded-2xl bg-slate-900 border border-slate-800 px-2 py-1 active:scale-95 transition"
          >
            <div className="text-lg lg:text-2xl">📜</div>
            <div className="mt-2 font-bold">History</div>
            <div className="text-xs text-slate-300">Previous Hunts</div>
          </button>

          <button
            onClick={(e) => {e.currentTarget.blur(); game.setScreen("leaderboard");}}
            className="rounded-2xl bg-slate-900 border border-slate-800 px-2 py-1 active:scale-95 transition"
          >
            <div className="text-lg lg:text-2xl">🏆</div>
            <div className="mt-2 font-bold">Leaderboard</div>
            <div className="text-xs text-slate-300">Top Hunters</div>
          </button>

          <button
            onClick={(e) => {e.currentTarget.blur(); game.setScreen("streaks");}}
            className="rounded-2xl bg-slate-900 border border-slate-800 px-2 py-1 active:scale-95 transition"
          >
            <div className="text-lg lg:text-2xl">🔥</div>
            <div className="mt-2 font-bold">Streaks</div>
            <div className="text-xs text-slate-300">Level Up Daily</div>
          </button>

          <button
            onClick={(e) => {e.currentTarget.blur(); game.setScreen("gumball");}}
            className="rounded-2xl bg-slate-900 border border-slate-800 px-2 py-1 active:scale-95 transition"
          >
            <div className="text-lg lg:text-2xl">🍬</div>
            <div className="mt-2 font-bold">Gumball</div>
            <div className="text-xs text-slate-300">Claim Rewards</div>
          </button>
        </div>
      </div>

      <div className="flex justify-center px-3 mt-2 text-center">
        <button
          onClick={(e) => {e.currentTarget.blur(); game.setScreen("tutorial");}}
          className="w-fit rounded-2xl border border-cyan-500/20 bg-slate-700 px-4 py-1 flex items-center justify-between active:scale-95 transition"
        >
          <div className="text-left">
            <div className="font-black text-cyan-300">❓ How To Play</div>
            <div className="text-xs text-slate-300 mt-1">
              Perfect for preschoolers & parents.
            </div>
          </div>
          <div className="text-xl lg:text-2xl">📖</div>
        </button>
      </div>

      <div className="px-2 mt-2">
        <div className="rounded-2xl bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 p-4">
          <div className="text-center text-sm lg:text-lg font-black text-emerald-300">
            🌟 Keep Hunting Every Day!
          </div>
          <div className="mt-2 text-center text-sm text-slate-400">
            Practice improves attention, memory, visual recognition and reaction speed.
          </div>
        </div>
      </div>

      <div className="pb-6 text-center">
        <div className="text-[11px] text-slate-200">
          Emoji Hunt • Preschool Edition
        </div>
        <div className="mt-1 text-[10px] text-slate-300">
          Built with ❤️ for Kids & Parents
        </div>
      </div>
    </div>
  );
}


/* ====================== GUMBALL ====================== */
function EmojiGumballScreen({ game }) {
  // Gumball expects history rows shaped as { totalScore }. EmojiGrid's log
  // rows track xp instead — mapped here so Gumball.jsx stays generic.
  const gumballHistory = (game.streakHistory || []).map((row) => ({
    totalScore: row.xp || 0,
  }));

  return (
    <Gumball
      gameKey="emojigrid"
      history={gumballHistory}
      streakInfo={{ currentStreak: game.currentStreak }}
      onClose={() => game.setScreen("home")}
    />
  );
}

export default function WonderEmojiHome() {
  const game = useEmojiGame();

  if (!game) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="text-center">
          <div className="text-5xl animate-bounce">😀</div>
          <div className="mt-3 text-slate-400">Loading Emoji Hunt...</div>
        </div>
      </div>
    );
  }

  // ================= ROUTER =================

  switch (game.screen) {
    case "home":
      return <WonderEmojiHomeScreen game={game} />;

    case "play":
      return <EmojiGameScreen game={game} />;

    case "result":
      return <EmojiResultScreen game={game} />;

    case "leaderboard":
      return <EmojiLeaderboard game={game} />;

    case "history":
      return <EmojiScoreHistory game={game} />;

    case "settings":
      return <EmojiSettings game={game} />;

    case "tutorial":
      return <EmojiTutorial game={game} />;

    case "daily":
      return <EmojiDailyChallenge game={game} />;

    case "streaks":
      return <EmojiStreak game={game} />;

    case "gumball":
      return <EmojiGumballScreen game={game} />;

    case "rewardhistory":
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <GumballHistoryPage onClose={() => game.setScreen("home")} />
        </div>
      );      

    default:
      return <WonderEmojiHomeScreen game={game} />;
  }
}